import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Building2, LineChart, Wallet } from "lucide-react";
import { cn } from "@/utils/cn";
import { formatCurrency, formatNumber } from "@/utils/format";
import { Loader } from "@/components/ui/Loader";
import { Select } from "@/components/ui/Select";
import { ChaoObraAvancoChart } from "./ChaoObraAvancoChart";
import {
  buildChaoAvancoChartSeries,
  monthDeltaPp,
  applyFinancialDeduction,
  pctAvancoFinanceiro,
  type ChaoAvancoGranularity,
} from "./chaoAvancoFinanceiroUtils";
import { formatObraDurationMessage } from "./chaoUcStageDuration";
import {
  fetchChaoUcStageSchedules,
  type ChaoPhysicalMonthPoint,
  type ChaoPhysicalWeekPoint,
  type ChaoUcStageScheduleDto,
} from "@/services/chaoDeObra";
import type { OutcomeItem } from "@/services/financeiro";

const CHART_H = 200;

const CHART_VIEW_OPTIONS = [
  { value: "m-7", label: "Últimos 7 meses", group: "Mensal" },
  { value: "m-12", label: "Últimos 12 meses", group: "Mensal" },
  { value: "m-24", label: "Últimos 24 meses", group: "Mensal" },
  { value: "w-8", label: "Últimas 8 semanas", group: "Semanal" },
  { value: "w-12", label: "Últimas 12 semanas", group: "Semanal" },
  { value: "w-26", label: "Últimas 26 semanas", group: "Semanal" },
];

function parseChartView(value: string): { granularity: ChaoAvancoGranularity; count: number } {
  const [g, n] = value.split("-");
  const count = parseInt(n ?? "", 10);
  if (g === "w") return { granularity: "week", count: Number.isFinite(count) && count > 0 ? count : 12 };
  return { granularity: "month", count: Number.isFinite(count) && count > 0 ? count : 7 };
}

interface Props {
  generalPct: number;
  gerencialTotal: number;
  financialDeduction?: number;
  valorTotalImoveis: number;
  avgConfigured: boolean;
  totalUnits: number;
  /** Unidades com cartão carregado (pode ser menor que totalUnits se o Sienge omitir id). */
  cardsLoaded?: number;
  physicalSeries: ChaoPhysicalMonthPoint[];
  physicalWeekSeries?: ChaoPhysicalWeekPoint[];
  outcome: OutcomeItem[];
  appliedCostCenter: string[];
  loadingFinancial?: boolean;
  onOpenPhysicalByUc?: () => void;
  /** Incrementa ao fechar o modal de datas das etapas para recarregar o prazo da obra. */
  scheduleRefreshKey?: number;
}

function DeltaBadge({
  delta,
  variant,
  compareLabel,
}: {
  delta: number | null;
  variant: "blue" | "green";
  compareLabel: string;
}) {
  if (delta == null || delta === 0) return null;
  const sign = delta > 0 ? "+" : "";
  const styles =
    variant === "blue"
      ? "bg-blue-50 text-blue-700 border-blue-100"
      : "bg-emerald-50 text-emerald-700 border-emerald-100";
  return (
    <span
      className={cn(
        "inline-flex items-center mt-1.5 px-2 py-0.5 rounded-md text-[10px] font-semibold border tabular-nums",
        styles,
      )}
    >
      {sign}
      {formatNumber(delta, 1)} p.p. vs {compareLabel}
    </span>
  );
}

function KpiCard({
  label,
  value,
  children,
  icon,
  iconWrap,
  barPct,
  barClass,
  delta,
  deltaVariant,
  deltaCompareLabel,
  onIconClick,
  iconTitle,
}: {
  label: string;
  value: string;
  children?: ReactNode;
  icon: ReactNode;
  iconWrap: string;
  barPct?: number;
  barClass: string;
  delta?: number | null;
  deltaVariant?: "blue" | "green";
  deltaCompareLabel?: string;
  onIconClick?: () => void;
  iconTitle?: string;
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3.5 shadow-card flex flex-col justify-between h-full min-h-[220px]">
      <div>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 leading-tight">
              {label}
            </p>
            <p className="mt-1.5 text-2xl font-bold text-slate-900 tabular-nums leading-none">{value}</p>
            {deltaVariant != null && (
              <DeltaBadge
                delta={delta ?? null}
                variant={deltaVariant}
                compareLabel={deltaCompareLabel ?? "mês anterior"}
              />
            )}
          </div>
          {onIconClick ? (
            <button
              type="button"
              onClick={onIconClick}
              title={iconTitle}
              className={cn(
                "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors",
                "hover:ring-2 hover:ring-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-300",
                iconWrap,
              )}
            >
              {icon}
            </button>
          ) : (
            <div
              className={cn(
                "w-10 h-10 rounded-xl flex items-center justify-center shrink-0",
                iconWrap,
              )}
            >
              {icon}
            </div>
          )}
        </div>
        {children && <div className="mt-2 space-y-0.5">{children}</div>}
      </div>
      {barPct != null && (
        <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden">
          <div
            className={cn("h-full rounded-full transition-all duration-500", barClass)}
            style={{ width: `${Math.min(100, Math.max(0, barPct))}%` }}
          />
        </div>
      )}
    </div>
  );
}

export function ChaoObraResumoFaixa({
  generalPct,
  gerencialTotal,
  financialDeduction = 0,
  valorTotalImoveis,
  avgConfigured,
  totalUnits,
  physicalSeries,
  physicalWeekSeries = [],
  outcome,
  appliedCostCenter,
  loadingFinancial,
  cardsLoaded,
  onOpenPhysicalByUc,
  scheduleRefreshKey = 0,
}: Props) {
  const [chartView, setChartView] = useState("m-7");
  const { granularity, count: periodCount } = parseChartView(chartView);
  const gerencialAjustado = applyFinancialDeduction(gerencialTotal, financialDeduction);
  const deltaCompareLabel = granularity === "week" ? "semana anterior" : "mês anterior";

  const chartData = useMemo(
    () =>
      buildChaoAvancoChartSeries(
        outcome,
        appliedCostCenter,
        granularity === "week" ? physicalWeekSeries : physicalSeries,
        valorTotalImoveis,
        periodCount,
        financialDeduction,
        granularity,
      ),
    [
      outcome,
      appliedCostCenter,
      granularity,
      physicalWeekSeries,
      physicalSeries,
      valorTotalImoveis,
      periodCount,
      financialDeduction,
    ],
  );

  const fisico = Math.min(100, Math.max(0, generalPct));
  const fin = avgConfigured
    ? pctAvancoFinanceiro(gerencialAjustado, valorTotalImoveis)
    : 0;
  const fisicoDelta = monthDeltaPp(chartData, "fisico");
  const finDelta = monthDeltaPp(chartData, "financeiro");

  const finDisplay = avgConfigured ? `${formatNumber(fin, 1)}%` : "—";
  const [schedules, setSchedules] = useState<Record<string, ChaoUcStageScheduleDto>>({});

  useEffect(() => {
    const ccs = appliedCostCenter.map((x) => String(x).trim()).filter(Boolean);
    if (!ccs.length) {
      setSchedules({});
      return;
    }
    let cancelled = false;
    void Promise.all(ccs.map((cc) => fetchChaoUcStageSchedules(cc)))
      .then((results) => {
        if (cancelled) return;
        const next: Record<string, ChaoUcStageScheduleDto> = {};
        for (const res of results) {
          if (!res.success) continue;
          const cc = res.costCenterId ?? "";
          for (const [label, sch] of Object.entries(res.schedules ?? {})) {
            next[`${cc}::${label}`] = sch;
          }
        }
        setSchedules(next);
      })
      .catch(() => {
        if (!cancelled) setSchedules({});
      });
    return () => {
      cancelled = true;
    };
  }, [appliedCostCenter, scheduleRefreshKey]);

  const obraDurationMsg = useMemo(() => formatObraDurationMessage(schedules), [schedules]);

  return (
    <div className="grid grid-cols-1 xl:grid-cols-6 gap-3 items-stretch">
      <div className="xl:col-span-2">
        <KpiCard
          label="Avanço físico da obra"
          value={`${formatNumber(fisico, 1)}%`}
          icon={<LineChart className="w-5 h-5" aria-hidden />}
          iconWrap="bg-blue-50 text-blue-600"
          barPct={fisico}
          barClass="bg-blue-500"
          delta={fisicoDelta}
          deltaVariant="blue"
          deltaCompareLabel={deltaCompareLabel}
          onIconClick={onOpenPhysicalByUc}
          iconTitle="Ver média do avanço físico por unidade construtiva"
        >
          {obraDurationMsg ? <p className="text-xs text-slate-600">{obraDurationMsg}</p> : null}
        </KpiCard>
      </div>

      <div className="xl:col-span-1">
        <KpiCard
          label="Avanço financeiro"
          value={loadingFinancial ? "…" : finDisplay}
          icon={<Wallet className="w-5 h-5" aria-hidden />}
          iconWrap="bg-emerald-50 text-emerald-600"
          barPct={avgConfigured ? Math.min(100, fin) : undefined}
          barClass="bg-emerald-500"
          delta={avgConfigured ? finDelta : null}
          deltaVariant="green"
          deltaCompareLabel={deltaCompareLabel}
        >
          {!loadingFinancial && avgConfigured && valorTotalImoveis > 0 && (
            <>
              <p className="text-[11px] text-slate-600 tabular-nums">
                <span className="text-slate-500">Valor realizado</span>{" "}
                <strong className="text-slate-800">{formatCurrency(gerencialAjustado)}</strong>
                {financialDeduction > 0 && (
                  <span className="text-slate-400 ml-1">
                    (antes: {formatCurrency(gerencialTotal)})
                  </span>
                )}
              </p>
              <p className="text-[11px] text-slate-600 tabular-nums">
                <span className="text-slate-500">Valor orçado</span>{" "}
                <strong className="text-slate-800">{formatCurrency(valorTotalImoveis)}</strong>
              </p>
            </>
          )}
          {!loadingFinancial && !avgConfigured && (
            <p className="text-[11px] text-slate-500">Configure o valor médio da unidade</p>
          )}
        </KpiCard>
      </div>

      <div className="xl:col-span-1">
        <KpiCard
          label="Total de unidades"
          value={formatNumber(totalUnits)}
          icon={<Building2 className="w-5 h-5" aria-hidden />}
          iconWrap="bg-slate-100 text-slate-500"
          barClass="bg-slate-300"
        >
          <p className="text-[11px] text-slate-500">Unidades de imóvel nesta obra</p>
          {cardsLoaded != null && cardsLoaded > 0 && cardsLoaded < totalUnits && (
            <p className="text-[10px] text-amber-700 mt-1">
              {cardsLoaded} cartões carregados no painel
            </p>
          )}
        </KpiCard>
      </div>

      <div className="xl:col-span-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-card flex flex-col min-h-[200px]">
        <div className="flex items-center justify-between gap-2 shrink-0 mb-1">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            Evolução — físico × financeiro
          </p>
          <Select
            options={CHART_VIEW_OPTIONS}
            value={chartView}
            onChange={(e) => setChartView(e.target.value)}
            className="!py-1 !pl-2 !pr-7 !text-[11px] !min-h-0 !h-7 !w-auto max-w-[196px] border-slate-200 rounded-lg"
            aria-label="Forma de ver a evolução"
          />
        </div>
        <div className="flex-1 min-h-0">
          {loadingFinancial ? (
            <div
              className="flex items-center justify-center h-full"
              style={{ minHeight: CHART_H }}
            >
              <Loader size="md" label="Carregando dados do Gerencial…" />
            </div>
          ) : (
            <ChaoObraAvancoChart data={chartData} height={CHART_H} />
          )}
        </div>
      </div>
    </div>
  );
}
